import React, { useState, useEffect } from "react";
import "./index.css";
import { getApiData } from "../../utils/APIHelper";
import { useSelector } from "react-redux";
import {
  Typography,
  CircularProgress,
  Stack,
  styled,
  useMediaQuery,
  Box,
} from "@mui/material";
import BalanceDetails from "./components/BalanceDetails";
import ProjectStats from "./components/ProjectStats";
import BalanceDetailsMobile from "./components/BalanceDetailsMobile";
import { updateUserData } from "../../utils/CommonFunction";
import { useTheme } from "@emotion/react";
import { useTokenValidation } from "../../components/hooks/useTokenValidation";
import SecurityPolicyModal from "../../components/SecurityPolicyModal";

const StyledHeading = styled("h2")({
  color: "#0D0D0E",
  fontFamily: "Inter Tight",
  fontSize: "24px",
  fontWeight: "500",
});

const MobileHeading = styled(Typography)(({ theme }) => ({
  fontSize: "22px",
  fontWeight: "600",
  color: "#0D0D0E",
  fontFamily: "Inter Tight",
  letterSpacing: "0",
}));

const MobileHeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "2vh 2vw 0 2vw",
}));

export default function ProjectBalance() {
  const { userData } = useSelector((state) => state.auth);
  const [projectDetails, setProjectDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Token validation hook
  const { showSecurityModal, handleSecurityModalClose } = useTokenValidation();

  useEffect(() => {
    updateUserData();
  }, []);

  useEffect(() => {
    if (userData?.contractor_data?.id) {
      fetchData();
    }
  }, [userData?.contractor_data?.id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getApiData(
        `api/v2/contractors/${userData.contractor_data.id}/balanceSheet`,
        "GET",
        {},
        {},
        true
      );
      setProjectDetails(response?.projectBalanceSheet || []);
    } catch (error) {
      console.error("Error fetching payment details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData?.contractor_data?.id) {
      fetchSummary();
    }
  }, [userData?.contractor_data?.id]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      let filters = `contractorId:=:${userData.contractor_data.id}&sortBy=dueDate&sortOrder=ASC`;

      const response = await getApiData(
        `api/v2/dashboard/paymentOverview/summary?object=payouts&filters=${filters}`,
        "GET",
        {},
        {},
        true
      );
      setSummary(response || {});
    } catch (error) {
      console.error("Error fetching payment details:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack
      flex={1}
      sx={{
        fontFamily: "Inter Tight",
      }}
    >
      <div style={{ background: "#fff", padding: isMobile && "1rem" }}>
        {isMobile ? (
          <MobileHeaderContainer>
            <MobileHeading>Project Balance</MobileHeading>
          </MobileHeaderContainer>
        ) : (
          <div
            style={{
              padding: "4vh 2vw 2vh 2vw",
              background:
                "linear-gradient(180deg, rgba(77, 107, 246, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
            }}
          >
            <StyledHeading>Project Balance</StyledHeading>
          </div>
        )}
        {!isMobile && <br />}
        <ProjectStats summary={summary} isMobile={isMobile} />
        <Box sx={{ padding: "0 1.5vw" }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 200,
              }}
            >
              <CircularProgress />
            </Box>
          ) : projectDetails?.length > 0 ? (
            <>
              {isMobile ? (
                <BalanceDetailsMobile
                  details={projectDetails}
                  fetchData={fetchData}
                />
              ) : (
                <BalanceDetails
                  details={projectDetails}
                  fetchData={fetchData}
                />
              )}
            </>
          ) : (
            <>
              <Typography
                variant="body1"
                sx={{ textAlign: "center", marginTop: 4 }}
              >
                No data available
              </Typography>
            </>
          )}
        </Box>
      </div>
      {/* Security Policy Modal */}
      <SecurityPolicyModal
        open={showSecurityModal}
        onConfirm={handleSecurityModalClose}
      />
    </Stack>
  );
}
